import { Box, Button, Link, Text } from 'native-base'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client'
import { useGateValue } from '@statsig/react-bindings'

import { AppContext } from '../context/AppContext'
import { AuthContext } from '../context/AuthContext'
import { OverlayState } from '../context/OverlayContext'
import {
  GET_ORDER_BY_TRACKING_ID_CWA,
  LOGIN_WITH_TOKEN,
  type LoginWithTokenMutation,
  type LoginWithTokenMutationVariables,
  type Order,
} from '@consumer/graphql'
import {
  shouldAutoOpenAccessCodeModal,
  createCanonicalContext,
  decorateCanonical,
  emitCanonical,
} from '@consumer/shared'
import { analytics, trackEvent } from '../utils/analytics-v2'
import '../utils/canonicalLog'
import { STATSIG_FEATURE_GATES } from '@consumer/shared'
import { signInWithToken } from '../utils/firebase'
import { CMABanner } from './CMABanner'
import DriverInfoModal, { type DriverInfoStage } from './DriverInfoModal/DriverInfoModal'
import FAQ from './FAQ'
import Loader from './Loader'
import { SearchResult } from './SearchResult'

const SearchResultsPage = () => {
  const { trackingId } = useParams()
  const [searchParams] = useSearchParams()
  const { order, setOrder, clearOrder } = useContext(AppContext)
  const { firebaseUser } = useContext(AuthContext)
  const { toggleModal } = useContext(OverlayState)
  const navigate = useNavigate()
  const includeConsumerSupportCases = useGateValue(STATSIG_FEATURE_GATES.cwa_support_escalation)
  // CBP-3873 gated rollout. When on, broadens the auto-open trigger from
  // `!hasInstructions` to `!hasCode`, capturing consumers who already have
  // delivery instructions but no access code on file.
  const noCodeTriggerEnabled = useGateValue(STATSIG_FEATURE_GATES.cwa_access_code_no_code_trigger)
  const hasAutoOpened = useRef(false)
  const trackingLoadOpIdRef = useRef<string>('')
  const trackingLoadStartMsRef = useRef<number>(0)
  // Fake-door experiment (driver info): capture the one-stop-away SMS link param
  // during the first render — the token-login effect below immediately replaces
  // the URL with a clean one, stripping all query params.
  const [showDriverInfoModal, setShowDriverInfoModal] = useState(() => searchParams.get('driverInfo') === '1')
  const [driverInfoStage, setDriverInfoStage] = useState<DriverInfoStage>('ask')
  const hasTrackedDriverInfoView = useRef(false)
  const hasTrackedDriverInfoInterest = useRef(false)

  const { loading, error, data } = useQuery<{
    order: Order
  }>(GET_ORDER_BY_TRACKING_ID_CWA, {
    variables: { trackingId, includeConsumerSupportCases },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !!searchParams.get('token'), // Skip first load if token is present
    onError: error => {
      const opId = trackingLoadOpIdRef.current
      if (opId) {
        decorateCanonical(opId, {
          queryDurationMs: Date.now() - trackingLoadStartMsRef.current,
          errorType: error?.message ?? 'unknown',
          isAuthenticated: !!firebaseUser,
          platform: 'web',
        })
        emitCanonical(opId, 'failure')
        trackingLoadOpIdRef.current = ''
      }
      if (!error.message.includes('Not Authorized')) {
        trackEvent('track_package_error', {
          error_reason: 'unknown',
          tracking_code: trackingId ?? '',
          features: 'cwa_map_view',
          page_type: 'tracking_page',
        })
      }
    },
  })

  const [loginWithTokenMutation, { loading: tokenLoading, error: tokenError }] = useMutation<
    LoginWithTokenMutation,
    LoginWithTokenMutationVariables
  >(LOGIN_WITH_TOKEN, {
    errorPolicy: 'all',
    onCompleted: async data => {
      if (data?.loginWithToken) {
        try {
          await signInWithToken(data.loginWithToken)
          trackEvent('token_login_success', { features: 'cwa_token_auth' })
        } catch {
          trackEvent('token_login_error', { error_reason: 'firebase_error', features: 'cwa_token_auth' })
        }
      }
      // After signing in, navigate to clean URL without token
      if (trackingId) {
        navigate(`/trackingId/${trackingId}`, { replace: true })
      }
    },
    onError: () => {
      trackEvent('token_login_error', { error_reason: 'internal_error', features: 'cwa_token_auth' })
      // If sign in fails, navigate to clean URL without token
      if (trackingId) {
        navigate(`/trackingId/${trackingId}`, { replace: true })
      }
    },
    // Refetch the order query after login with token
    refetchQueries: [GET_ORDER_BY_TRACKING_ID_CWA],
  })

  analytics.page({ title: 'Tracker | Veho | New' })

  useEffect(() => {
    // Create the canonical context for the direct (non-token) load path.
    // When a token is present the query is skipped on first render; the context is
    // created lazily in the data effect after the token-login flow redirects.
    if (!searchParams.get('token')) {
      const opId = crypto.randomUUID()
      trackingLoadOpIdRef.current = opId
      trackingLoadStartMsRef.current = Date.now()
      createCanonicalContext('CanonicalTrackingLoad', opId, {
        platform: 'web',
        isAuthenticated: !!firebaseUser,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data?.order) {
      // On the token-login cold-start path the mount effect skips context creation
      // (token was present). After loginWithTokenMutation redirects to the clean URL,
      // useQuery fires for the first time and lands here. Create the context lazily
      // so this path also gets canonical telemetry.
      if (!trackingLoadOpIdRef.current) {
        const opId = crypto.randomUUID()
        trackingLoadOpIdRef.current = opId
        // Start time was captured at render when token was no longer present, or
        // defaults to now if the redirect happened between renders.
        if (trackingLoadStartMsRef.current === 0) {
          trackingLoadStartMsRef.current = Date.now()
        }
        createCanonicalContext('CanonicalTrackingLoad', opId, {
          platform: 'web',
          isAuthenticated: !!firebaseUser,
        })
      }

      const opId = trackingLoadOpIdRef.current
      if (opId) {
        decorateCanonical(opId, {
          queryDurationMs: Date.now() - trackingLoadStartMsRef.current,
          isAuthenticated: !!firebaseUser,
          orderStatus: data.order.calculatedOrderInformation?.orderStatus ?? undefined,
        })
        emitCanonical(opId, 'success')
        trackingLoadOpIdRef.current = ''
      }
      trackEvent('track_package', {
        tracking_code: trackingId ?? '',
        features: 'cwa_map_view',
        veho_client_id: data?.order?.clientId ?? '',
        is_authenticated: !!firebaseUser,
        page_type: 'tracking_page',
      })
      setOrder(data.order as Order)
    }
  }, [data, setOrder, trackingId, firebaseUser])

  useEffect(() => {
    // The driver-info fake-door popup takes priority: while it is showing,
    // hold off the access-code/instructions auto-open. When the consumer
    // dismisses it, this effect re-runs and the auto-open proceeds as usual.
    if (!data?.order || hasAutoOpened.current || showDriverInfoModal) return

    const { hasInstructions, instructionEditsAllowed, hasCode } = data.order

    const shouldOpen = shouldAutoOpenAccessCodeModal({
      hasInstructions,
      instructionEditsAllowed,
      // Server-computed presence boolean (CBP-3873). Do NOT derive from address.securityCode:
      // that value is redacted to null for unauthenticated visitors, so deriving client-side
      // would re-prompt consumers who already have a code on file.
      hasCode: !!hasCode,
      noCodeTriggerEnabled,
    })

    if (shouldOpen) {
      hasAutoOpened.current = true
      toggleModal({ showModal: true, auth_flow: 'instructions' })
    }
  }, [data?.order, noCodeTriggerEnabled, toggleModal, showDriverInfoModal])

  // Fake-door experiment (driver info): count each click-through from the SMS link.
  useEffect(() => {
    if (showDriverInfoModal && !hasTrackedDriverInfoView.current) {
      hasTrackedDriverInfoView.current = true
      trackEvent('driver_info_popup_viewed', {
        tracking_code: trackingId ?? '',
        page_type: 'tracking_page',
      })
    }
  }, [showDriverInfoModal, trackingId])

  // Fake-door experiment (driver info): the unambiguous demand signal. Arriving
  // on the page only means the consumer tapped the tracking link, which they may
  // have done for their ETA; pressing this CTA is a deliberate answer to the
  // question, so it is what we count as interest.
  const onDriverInfoConfirm = () => {
    setDriverInfoStage('confirmed')
    if (hasTrackedDriverInfoInterest.current) return
    hasTrackedDriverInfoInterest.current = true
    trackEvent('driver_info_interest_confirmed', {
      tracking_code: trackingId ?? '',
      page_type: 'tracking_page',
    })
  }

  // Declining is a real answer, not a dismissal: it separates "asked and said no"
  // from "never engaged", which is what closing the popup outright records.
  const onDriverInfoDecline = () => {
    setShowDriverInfoModal(false)
    if (hasTrackedDriverInfoInterest.current) return
    hasTrackedDriverInfoInterest.current = true
    trackEvent('driver_info_interest_declined', {
      tracking_code: trackingId ?? '',
      page_type: 'tracking_page',
    })
  }

  useEffect(() => {
    if (!trackingId) {
      return
    }

    const token = searchParams.get('token')

    // If token is present and user is not logged in, login with token.
    // Capture start time now so queryDurationMs in the data effect measures
    // the full auth+query round-trip, not just the moment data arrives.
    if (token && !firebaseUser) {
      // Create the canonical context now so onError can emit a failure even if
      // the order query that fires after the redirect never returns data.
      // Both the context creation and start-time capture are guarded so repeat
      // effect runs (e.g. when firebaseUser or loginWithTokenMutation changes)
      // do not overwrite the opId or corrupt queryDurationMs.
      if (!trackingLoadOpIdRef.current) {
        trackingLoadStartMsRef.current = Date.now()
        const opId = crypto.randomUUID()
        trackingLoadOpIdRef.current = opId
        createCanonicalContext('CanonicalTrackingLoad', opId, {
          platform: 'web',
          isAuthenticated: false,
        })
      }
      loginWithTokenMutation({ variables: { token } })
      return
    }

    // If token is present and user is logged in, navigate to clean URL without token
    navigate(`/trackingId/${trackingId}`, { replace: true })
  }, [trackingId, searchParams, firebaseUser, loginWithTokenMutation, navigate])

  // GestureResponderEvent not exported by NativeBase
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onBackToSearch = (e: any) => {
    e.preventDefault()
    trackEvent('return_to_tracking_page', { features: 'cwa_map_view' })
    clearOrder()
    navigate('/')
  }

  return (
    <>
      {(loading || tokenLoading) && <Loader />}
      {tokenError && (
        <Box backgroundColor="red.100" padding={4} borderRadius={8} marginBottom={4}>
          <Text color="red.800" fontSize="sm">
            Authentication failed. Please check your login link and try again.
          </Text>
        </Box>
      )}
      {error &&
        !error?.message?.includes('Not Authorized') &&
        !error?.message?.includes('401') &&
        !error?.message?.includes(
          `Cannot return null for non-nullable type: 'String' within parent 'DeliveryDate' (/order/deliveryDateInfo/date)`
        ) && (
          <Box
            style={{ margin: 'auto', flexDirection: 'column', alignItems: 'center' }}
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
          >
            <Text style={{ color: '#7B0000', maxWidth: 400, margin: 20 }}>
              Looks like there was an issue fetching the tracking info. Please try reloading the page.
            </Text>
            <Button
              onPress={() => window.location.reload()}
              borderRadius={100}
              bgColor="black"
              width="250px"
              height="32px"
            >
              Reload Page
            </Button>
          </Box>
        )}
      {error && error?.message?.includes('401') && (
        <Box
          style={{ margin: 'auto', flexDirection: 'column', alignItems: 'center' }}
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Text style={{ color: '#7B0000', maxWidth: 400, margin: 20 }}>
            Looks like there was an issue fetching the tracking info. Please check your network and disable any content
            blockers, then try reloading the page. If the issue persists, text <Link href="sms:68953">68953</Link> to
            contact support.
          </Text>
        </Box>
      )}
      {!loading && !error && data?.order === null && (
        <Box style={{ margin: 'auto' }} display="flex" justifyContent="center" alignItems="flex-start">
          <Text style={{ color: '#7B0000', maxWidth: 400, margin: 20 }}>
            We can't find a package associated with that number. Please double check your tracking ID or Barcode. If you
            need help, text <Link href="sms:68953">68953</Link> to contact support.
          </Text>
        </Box>
      )}
      {!loading && !tokenLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="stretch"
          alignContent="stretch"
          flexDirection="row"
          px={3}
          pt={1}
          pb={4}
          width="100%"
        >
          <div id="searchResults">
            <Box>
              {data?.order !== null && order?.orderId && <SearchResult order={order} onBackToSearch={onBackToSearch} />}
            </Box>
          </div>
        </Box>
      )}
      <Box mt={5} width={'100%'}>
        <CMABanner />
      </Box>
      <FAQ />
      <DriverInfoModal
        visible={showDriverInfoModal}
        stage={driverInfoStage}
        onConfirm={onDriverInfoConfirm}
        onDecline={onDriverInfoDecline}
        onClose={() => setShowDriverInfoModal(false)}
      />
    </>
  )
}

export default SearchResultsPage
